import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import "./home.css";
import Hero from "../components/hero/hero";
import Services from "../components/services/services";
import FlowChart from "../components/flowchart/flowchart";
import SEO from "../components/seo";

function Home() {
  const { t } = useTranslation();
  const location = useLocation();
  const aboutFeatures = t("about.features", { returnObjects: true });
  const teamMembers = t("team.members", { returnObjects: true });

  useEffect(() => {
    // Check if there's a hash in the URL
    if (location.hash) {
      // Get the section ID from the hash (remove the # character)
      const sectionId = location.hash.substring(1);

      // Give the DOM a moment to fully render before scrolling
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          const headerOffset = 15; // Adjust based on your header height
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition =
            elementPosition + window.pageYOffset - headerOffset;

          window.scrollTo({
            top: offsetPosition,
            behavior: "smooth",
          });
        }
      }, 100);
    }
  }, [location]);

  const handleDirectionsClick = () => {
    // Open maps app based on device
    if (/Android|iPhone|iPad/i.test(navigator.userAgent)) {
      window.open(
        `https://www.google.com/maps/place/Dr+William+Mok+Orthodontic+Center/@22.2819611,114.1568384,750m/data=!3m2!1e3!4b1!4m6!3m5!1s0x34040064bd51fc75:0xf32c6d3ceb8cd52c!8m2!3d22.2819611!4d114.1568384!16s%2Fg%2F1xg5q8hr?entry=ttu&g_ep=EgoyMDI2MDEwNi4wIKXMDSoKLDEwMDc5MjA3M0gBUAM%3D"`
      );
    } else {
      window.open(
        `https://www.google.com/maps/place/Dr+William+Mok+Orthodontic+Center/@22.2819611,114.1568384,750m/data=!3m2!1e3!4b1!4m6!3m5!1s0x34040064bd51fc75:0xf32c6d3ceb8cd52c!8m2!3d22.2819611!4d114.1568384!16s%2Fg%2F1xg5q8hr?entry=ttu&g_ep=EgoyMDI2MDEwNi4wIKXMDSoKLDEwMDc5MjA3M0gBUAM%3D"`
      );
    }
  };

  return (
    <>
      <SEO
        title="Ortho Specialist | Metal Braces & Clear Aligners"
        description="Expert orthodontic care with braces and clear aligners for all ages. Book your consultation today."
        keywords="braces, clear aligners, orthodontist, teeth straightening, central dental clinic"
        canonical="https://www.orthospecialist.hk"
        image="https://www.orthospecialist.hk/assets/images/logo.png"
      />

      <div className="home-page">
        <Hero />

        <section id="about" className="about">
          <div className="container">
            <div className="about-content">
              <div className="about-text">
                <h2>{t("about.title")}</h2>
                <p className="lead">{t("about.subtitle")}</p>
                <div className="about-details">
                  {Array.isArray(aboutFeatures) &&
                    aboutFeatures.map((feature, index) => (
                      <div key={index} className="feature">
                        <i className={`icon ${feature.icon}`}></i>
                        <h3>{feature.title}</h3>
                        <p>{feature.text}</p>
                      </div>
                    ))}
                </div>
              </div>
              <div className="about-image">
                <img
                  src="/assets/images/about.jpg"
                  alt={t("about.imageAlt")}
                  width={500}
                  height={600}
                />
              </div>
            </div>
          </div>
        </section>

        {/* Team Section*/}
        <section className="team-section" id="team">
          <div className="container">
            <h2 className="section-title">{t("team.title")}</h2>
            {/* <p className="section-subtitle">{t("team.subtitle")}</p> */}
            <div className="team-grid">
              {Array.isArray(teamMembers) &&
                teamMembers.map((member) => (
                  <div key={member.id} className="team-card">
                    {/* <div className="team-image">
                    <img
                      src={`/images/team/${member.photo}`}
                      alt={member.name}
                      width={300}
                      height={300}
                    />
                  </div> */}
                    <h3>{member.name}</h3>
                    <p className="position">{member.position}</p>
                    <p className="qualification">{member.qualification}</p>
                    <p className="qualification2">{member.qualification2}</p>
                    <p className="qualification3">{member.qualification3}</p>
                    <p className="bio">{member.bio1}</p>
                    <p className="bio">{member.bio2}</p>
                  </div>
                ))}
            </div>
          </div>
        </section>

        <Services />

        <FlowChart />

        <section className="map-section" id="contact">
          <div className="container">
            <h2 className="section-title">{t("contact.location.title")}</h2>

            <div className="map-container">
              {/* Map - Will stack first on mobile */}
              <div className="map-wrapper" style={{ gridArea: "map" }}>
                <iframe
                  title={t("contact.location.mapTitle")}
                  src={`https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3264.0583461530755!2d114.15683840000001!3d22.2819611!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x34040064bd51fc75%3A0xf32c6d3ceb8cd52c!2sDr%20William%20Mok%20Orthodontic%20Center!5e1!3m2!1sen!2shk!4v1767977957194!5m2!1sen!2shk"`}
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  aria-hidden="false"
                  tabIndex="0"
                ></iframe>
              </div>

              <div className="location-details" style={{ gridArea: "details" }}>
                <h3>{t("contact.location.clinicName")}</h3>
                <div className="detail-item">
                  <div className="icon-circle">
                    <i className="fas fa-map-marker-alt" aria-hidden="true"></i>
                  </div>
                  <div>
                    <h4>{t("contact.location.address.title")}</h4>
                    <a href="https://www.google.com/maps/place/Dr+William+Mok+Orthodontic+Center/@22.2819611,114.1568384,750m/data=!3m2!1e3!4b1!4m6!3m5!1s0x34040064bd51fc75:0xf32c6d3ceb8cd52c!8m2!3d22.2819611!4d114.1568384!16s%2Fg%2F1xg5q8hr?entry=ttu&g_ep=EgoyMDI2MDEwNi4wIKXMDSoKLDEwMDc5MjA3M0gBUAM%3D">
                      <address>
                        {t("contact.location.address.line1")}
                        <br />
                        {t("contact.location.address.line2")}
                      </address>
                    </a>
                  </div>
                </div>

                <div className="detail-item">
                  <div className="icon-circle">
                    <i className="fas fa-phone-alt" aria-hidden="true"></i>
                  </div>
                  <div>
                    <h4>{t("contact.location.phone.title")}</h4>
                    <p>
                      <a href={`tel:${t("contact.location.phone.number")}`}>
                        {t("contact.location.phone.number")}
                      </a>
                    </p>
                  </div>
                </div>

                <div className="detail-item">
                  <div className="icon-circle">
                    <i className="fas fa-clock" aria-hidden="true"></i>
                  </div>
                  <div>
                    <h4>{t("contact.location.hours.title")}</h4>
                    <p>
                      <a
                        href="mailto:info@orthospecialist.hk"
                        className="footer-phone"
                      >
                        {t("footer.email")}
                      </a>
                    </p>
                  </div>
                </div>

                <button
                  className="cta-button"
                  onClick={handleDirectionsClick}
                  aria-label={t("contact.location.getDirections")}

                  data-type={t("contact.location.getDirections")}
                >
                  <i className="fas fa-directions" aria-hidden="true"></i>
                  {t("contact.location.getDirections")}
                </button>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}

export default Home;
